<template>
    <div class="post-slider">
        <div class="post-slider__wrapper">
            <div class="post-slider__bar post-slider__bar--left" @click="scrollLeft"
                :class="{ 'post-slider__bar--hidden': !canScrollLeft }">
                <i class="fas fa-chevron-left"></i>
            </div>
            <div class="post-slider__container" ref="container" @scroll="handleScroll">
                <div class="post-slider__items">
                    <div class="post-slider__item" v-for="post in sortedPosts" :key="post.id">
                        <router-link :to="`/blog/post/${post.slug}`" class="post-slider__link" @click="scrollToTop">
                            <div class="post-slider__card">
                                <div class="post-slider__content">
                                    <h3 class="post-slider__title">{{ post.title }}</h3>
                                    <p class="post-slider__description">{{ post.description }}</p>
                                    <hr />
                                </div>
                            </div>
                        </router-link>
                    </div>
                </div>
            </div>
            <div class="post-slider__bar post-slider__bar--right" @click="scrollRight"
                :class="{ 'post-slider__bar--hidden': !canScrollRight }">
                <i class="fas fa-chevron-right"></i>
            </div>
        </div>
    </div>
</template>

<script setup>
import { getBlogCollection } from '@/assets/markdowns/blog/BlogCollection.js';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const route = useRoute();
const { locale } = useI18n();
const container = ref(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(true);
const scrollAmount = 300;

const sortedPosts = computed(() => {
    if (!locale.value) {
        console.error('Locale is not defined');
        return [];
    }

    const posts = getBlogCollection(locale.value) || [];
    if (!Array.isArray(posts)) {
        console.error('Posts is not an array');
        return [];
    }

    return posts
        .filter(post => post?.pubDate !== null)
        .filter(post => post.slug !== route.params.id)
        .sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));
});

const handleScroll = () => {
    if (container.value) {
        const { scrollLeft, scrollWidth, clientWidth } = container.value;
        canScrollLeft.value = scrollLeft > 0;
        canScrollRight.value = scrollLeft + clientWidth < scrollWidth;
    }
};

const scrollLeft = () => {
    if (container.value && canScrollLeft.value) {
        container.value.scrollBy({
            left: -scrollAmount,
            behavior: 'smooth'
        });
    }
};

const scrollRight = () => {
    if (container.value && canScrollRight.value) {
        container.value.scrollBy({
            left: scrollAmount,
            behavior: 'smooth'
        });
    }
};

const scrollToTop = () => {
    const target = document.getElementById('post-nav');
    if (target) {
        const yOffset = -280; // Ajusta según la altura de tu navbar
        const y = target.getBoundingClientRect().top + window.pageYOffset + yOffset;

        window.scrollTo({ top: y, behavior: 'smooth' });
    }
};

onMounted(() => {
    if (container.value) {
        // Inicializar el estado de los botones
        handleScroll();
        // Configurar el scroll infinito
        container.value.addEventListener('scroll', handleScroll);
    }
});
</script>

<style scoped>
hr {
    width: 100%;
    border: 0;
    height: 1px;
    background-color: #1b3b46;
    margin: 1rem 0;
    align-self: flex-end;
}

.post-slider {
    padding: 4rem 0;
    margin-top: 4rem;
}

.post-slider__wrapper {
    position: relative;
    max-width: 1200px;
    margin: 0 auto;
}

.post-slider__container {
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 2rem;
    scroll-behavior: smooth;
    -ms-overflow-style: none;
    scrollbar-width: none;
    transition: scroll-behavior 0.3s ease;
}

.post-slider__container::-webkit-scrollbar {
    display: none;
}

.post-slider__items {
    display: flex;
    gap: 2rem;
    padding: 1rem 0;
}

.post-slider__item {
    flex: 0 0 calc(33.333% - 6.33rem);
    min-width: calc(33.333% - 6.33rem);
}

.post-slider__link {
    text-decoration: none;
    color: inherit;
}

.post-slider__card {
    background: white;
    border-radius: 2rem;
    padding: 2rem;
    height: 100%;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.post-slider__items .post-slider__item:nth-child(odd) .post-slider__card {
    background: var(--PrimaryColor) !important;
    color: var(--SecondaryColor) !important;
}

.post-slider__card:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.post-slider__content {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.post-slider__title {
    font-size: 2rem !important;
    font-weight: 800 !important;
    color: var(--ComplementaryColor04);
    margin-bottom: 1rem;
}

.post-slider__description {
    font-size: 1.2rem !important;
    font-weight: 500 !important;
    color: var(--ComplementaryColor03);
    margin-bottom: 1.5rem;
    flex-grow: 1;
}

.post-slider__bar {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.686);
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 1;
    opacity: 1;
    visibility: visible;
}

.post-slider__bar--hidden {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
}

.post-slider__bar:hover {
    background: rgba(252, 235, 48, 0.752);
}

.post-slider__bar--left {
    left: 0;
    border-radius: 0 1rem 1rem 0;
}

.post-slider__bar--right {
    right: 0;
    border-radius: 1rem 0 0 1rem;
}

.post-slider__bar i {
    font-size: 1.5rem;
    color: var(--ComplementaryColor04);
}

@media (max-width: 768px) {
    .post-slider__items {
        flex-direction: column;
        gap: 1rem;
    }

    .post-slider__item {
        min-width: 100%;
    }

    .post-slider__title {
        font-size: 2rem;
    }

    .post-slider__bar {
        display: none;
    }

    .post-slider__card:hover {
        transform: none;
        box-shadow: none;
    }

    .post-slider__container {
        padding: 0;
    }

    .post-slider__items {
        gap: 5rem;
    }

    .post-slider {
        margin: 0;
    }
}
</style>